/**
 * Every exception, variance and waiver in the organization.
 *
 * Filters in the URL, as the other lists keep them. "Open" is the default
 * view — everything still live — because the closed ones are history and
 * the list is a working list.
 */

import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { hasAtLeast } from '../../types/roles';
import { FILTER_MAX } from '../../types/fieldLimits';
import { useAddons } from '../org/useAddons';
import { useTaxonomy } from '../admin/useTaxonomy';
import { Spinner } from '../../app/Spinner';
import { PageState } from '../../shared/PageState';
import { SegmentedChoice } from '../../shared/SegmentedChoice';
import { isCallFailure } from '../../lib/callable';
import { daysUntil, formatDay } from '../../shared/dates';
import { useExceptions } from './useExceptions';
import { filterExceptions, NO_FILTER, OPEN_STATUSES, type ExceptionFilter } from './filters';
import {
  EXCEPTION_STATUSES, EXCEPTION_TYPES, RISK_LEVELS,
  RISK_LABEL, RISK_TONE, STATUS_LABEL, STATUS_TONE, TYPE_LABEL,
  type ExceptionStatus, type ExceptionType, type RiskLevel,
} from './types';
import styles from '../../shared/Page.module.css';
import form from '../../shared/Form.module.css';
import own from './Exceptions.module.css';

export const LOCKED_TITLE = 'Exceptions and variances is not turned on';

function readFilter(p: URLSearchParams): ExceptionFilter {
  const status = p.get('status');
  const risk = p.get('risk');
  const type = p.get('type');
  return {
    query: p.get('q') ?? '',
    status: status === 'all' || status === 'open' ? status
      : (EXCEPTION_STATUSES as readonly string[]).includes(status ?? '') ? (status as ExceptionStatus) : 'open',
    risk: (RISK_LEVELS as readonly string[]).includes(risk ?? '') ? (risk as RiskLevel) : 'all',
    type: (EXCEPTION_TYPES as readonly string[]).includes(type ?? '') ? (type as ExceptionType) : 'all',
  };
}

function writeFilter(f: ExceptionFilter): URLSearchParams {
  const p = new URLSearchParams();
  if (f.query.trim()) p.set('q', f.query);
  if (f.status !== 'open') p.set('status', f.status);
  if (f.risk !== 'all') p.set('risk', f.risk);
  if (f.type !== 'all') p.set('type', f.type);
  return p;
}

/** "Expires" in words that carry the urgency. */
export function expiryText(iso: string | null, status: ExceptionStatus): { text: string; cls?: string } {
  if (!iso) return { text: '—' };
  const days = daysUntil(iso);
  if (days === null) return { text: formatDay(iso) };
  const live = status === 'active' || status === 'under_review' || status === 'approved';
  if (live && days < 0) return { text: `${formatDay(iso)} · past`, cls: 'past' };
  if (live && days <= 30) return { text: `${formatDay(iso)} · ${days} ${days === 1 ? 'day' : 'days'}`, cls: 'soon' };
  return { text: formatDay(iso) };
}

export function ExceptionListPage() {
  const { session } = useAuth();
  const orgId = session?.orgId ?? null;
  const roles = session?.roles ?? [];
  const author = hasAtLeast(roles, 'author');

  const addons = useAddons(orgId);
  const enabled = addons.data?.exceptionManagement === true;
  const list = useExceptions(orgId, enabled);
  const departments = useTaxonomy('department', enabled);
  const departmentName = useMemo(() => {
    const byId = new Map((departments.data ?? []).map((d) => [d.id, d.name]));
    return (id: string) => byId.get(id) ?? '';
  }, [departments.data]);

  const [params, setParams] = useSearchParams();
  const filter = useMemo(() => readFilter(params), [params]);
  const setFilter = (next: Partial<ExceptionFilter>) => setParams(writeFilter({ ...filter, ...next }), { replace: true });

  const rows = list.data ?? [];
  const shown = useMemo(() => filterExceptions(rows, filter, departmentName), [rows, filter, departmentName]);
  const openCount = rows.filter((r) => OPEN_STATUSES.includes(r.status)).length;
  const filtered = filter.query.trim() !== '' || filter.status !== 'open' || filter.risk !== 'all' || filter.type !== 'all';

  if (addons.isLoading) return <Spinner label="Loading" />;
  if (!enabled) {
    return (
      <>
        <header className={styles.header}><h1 className={styles.title}>Exceptions and variances</h1></header>
        <div className={styles.body}>
          <PageState title={LOCKED_TITLE}>
            This add-on is not enabled for your organization. Your StandingDocs administrator can turn it on.
          </PageState>
        </div>
      </>
    );
  }

  return (
    <>
      <header className={styles.header}>
        <div className={styles.headerRow}>
          <div>
            <h1 className={styles.title}>Exceptions and variances</h1>
            <p className={styles.subtitle}>
              Approved, time-limited departures from a policy, with the reason, the controls in
              place, and who signed. What a surveyor sees when practice and the policy differ.
            </p>
          </div>
          {author && (
            <div className={styles.actions}>
              <Link to="/exceptions/new" className={`${styles.btn} ${styles.btnPrimary}`}>New exception</Link>
            </div>
          )}
        </div>
      </header>

      <div className={styles.body}>
        {list.error && (
          <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">
            Could not load exceptions. {isCallFailure(list.error) ? list.error.message : ''}
          </div>
        )}

        {list.isLoading ? (
          <Spinner label="Loading exceptions" />
        ) : rows.length === 0 ? (
          <div className={`${styles.notice} ${styles.noticeWarn}`} role="status">
            No exceptions yet.{' '}
            {author ? 'Raise the first with New exception.' : 'An author raises one when practice must depart from a policy.'}
          </div>
        ) : (
          <>
            <div className={own.filters}>
              <div className={own.filterText}>
                <label className={form.label} htmlFor="exc-filter">Filter</label>
                <input id="exc-filter" className={form.input} type="text" maxLength={FILTER_MAX} autoComplete="off"
                  value={filter.query} onChange={(e) => setFilter({ query: e.target.value })}
                  placeholder="Title, policy or department" />
              </div>
              <div>
                <label className={form.label} htmlFor="exc-status">Status</label>
                <select id="exc-status" className={form.select} value={filter.status}
                  onChange={(e) => setFilter({ status: e.target.value as ExceptionFilter['status'] })}>
                  <option value="open">Open ({openCount})</option>
                  <option value="all">All ({rows.length})</option>
                  {EXCEPTION_STATUSES.map((s) => (
                    <option key={s} value={s}>{STATUS_LABEL[s]} ({rows.filter((r) => r.status === s).length})</option>
                  ))}
                </select>
              </div>
              <SegmentedChoice legend="Risk" name="exc-risk" value={filter.risk}
                onChange={(v) => setFilter({ risk: v })}
                options={[{ value: 'all' as const, label: 'Any' }, ...RISK_LEVELS.map((r) => ({ value: r, label: RISK_LABEL[r] }))]} />
              <SegmentedChoice legend="Type" name="exc-type" value={filter.type}
                onChange={(v) => setFilter({ type: v })}
                options={[{ value: 'all' as const, label: 'Any' }, ...EXCEPTION_TYPES.map((t) => ({ value: t, label: TYPE_LABEL[t] }))]} />
            </div>

            <p className={own.count} role="status" aria-live="polite">
              <span><b className="tnum">{shown.length}</b> of <span className="tnum">{rows.length}</span></span>
              {filtered && (
                <button type="button" className={own.linkBtn} onClick={() => setParams(writeFilter(NO_FILTER), { replace: true })}>
                  Clear filters
                </button>
              )}
            </p>

            <div className={own.tableWrap}>
              <table className={own.table}>
                <thead>
                  <tr>
                    <th scope="col">Exception</th>
                    <th scope="col">Type</th>
                    <th scope="col">Risk</th>
                    <th scope="col">Status</th>
                    <th scope="col">Expires</th>
                    <th scope="col">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r) => {
                    const exp = expiryText(r.expirationDate, r.status);
                    return (
                      <tr key={r.id}>
                        <td className={own.titleCell}>
                          <Link to={`/exceptions/${r.id}`}>{r.title}</Link>
                          <span className={own.sub}>{r.policyTitle}</span>
                        </td>
                        <td>{TYPE_LABEL[r.exceptionType]}</td>
                        <td><span className={`${styles.tag} ${styles[RISK_TONE[r.riskLevel]]}`}>{RISK_LABEL[r.riskLevel]}</span></td>
                        <td>
                          <span className={`${styles.tag} ${styles[STATUS_TONE[r.status]]}`}>{STATUS_LABEL[r.status]}</span>
                          {r.policyVersionChanged && <span className={own.sub}>Policy changed</span>}
                        </td>
                        <td className={`${own.num} ${exp.cls ? own[exp.cls] : ''}`}>{exp.text}</td>
                        <td className={`${own.num} ${r.updatedAt ? '' : own.muted}`}>{formatDay(r.updatedAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {shown.length === 0 && (
                <p className={styles.subtitle}>Nothing matches. <strong>Clear filters</strong> to see all {rows.length}.</p>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
